import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {useFundamentalMuiTheme} from '@hconnect/uikit'
import {usePlantIdFromHierarchyId} from '@hproduce/suite-core'
import {CircularProgress, ThemeProvider} from '@mui/material'
import {HrocChecklistsPathSegments} from '@widgets/hroc-checklists'
import {Suspense} from 'react'
import {Outlet, RouteObject} from 'react-router'

import {hrocChecklistsFeatureTranslations} from '../localization'
import {ChecklistPage, ChecklistsTemplatesPage, UnsupportedChecklistKindErrorPage} from '../pages'

const SuiteChecklistTemplates = () => {
  const plantId = usePlantIdFromHierarchyId()

  if (!plantId) {
    return null
  }

  return <ChecklistsTemplatesPage plantId={plantId} />
}

const SuiteChecklistTemplatesRouteWrapper = () => {
  return (
    <ThemeProvider theme={useFundamentalMuiTheme()}>
      <SuiteChecklistTemplates />
    </ThemeProvider>
  )
}

const SuiteChecklistPage = () => {
  const plantId = usePlantIdFromHierarchyId()

  if (!plantId) {
    return null
  }

  return <ChecklistPage plantId={plantId} />
}

const SuiteChecklistRouteWrapper = () => {
  return (
    <ThemeProvider theme={useFundamentalMuiTheme()}>
      <Suspense fallback={<CircularProgress color="secondary" size={80} />}>
        <SuiteChecklistPage />
      </Suspense>
    </ThemeProvider>
  )
}

/**
 * A composable sub-route for the checklist detail page.
 *
 * Designed to be nested inside any parent route whose page navigates to
 * `HrocChecklistsPathSegments.Checklist` (e.g. `EventTaskChecklist` component).
 * By embedding this sub-route, the checklist page becomes a child of the caller's
 * route tree, which preserves "back to" navigation and allows seamless integration
 * into other features without duplicating route definitions.
 *
 * Includes its own `FeatureTranslationsLoader` so it's self-contained — translations
 * are loaded regardless of whether the parent feature loads them.
 */
export const hrocChecklistsSubRoute: RouteObject = {
  path: HrocChecklistsPathSegments.Checklist,
  element: (
    <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
      <Outlet />
    </FeatureTranslationsLoader>
  ),
  children: [
    {
      index: true,
      element: <SuiteChecklistRouteWrapper />
    },
    {
      path: HrocChecklistsPathSegments.UnsupportedKind,
      element: <UnsupportedChecklistKindErrorPage />
    }
  ]
}

export const hrocChecklistTemplatesRoute: RouteObject = {
  path: '*',
  children: [
    {
      index: true,
      element: (
        <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
          <SuiteChecklistTemplatesRouteWrapper />
        </FeatureTranslationsLoader>
      )
    },
    hrocChecklistsSubRoute
  ]
}
